import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Alert, Card, Field, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { type DirectoryUser, type HireListItem, isHr, type SeparationListItem } from './data';
import { PeopleNav } from './PeopleNav';
import { ResetActions } from './ResetActions';

/** Portal de autoservicio de RR. HH. (guía 7.5): alta, baja, reset de MFA y reset de contraseña. */
export function HrPortalPage() {
  const hires = useQuery({ queryKey: ['usuarios', 'altas', 'lista', 'por_aprobar', 'pendiente'], queryFn: () => api<HireListItem[]>('/api/usuarios/altas?alcance=por_aprobar&estado=pendiente') });
  const seps = useQuery({ queryKey: ['usuarios', 'bajas', 'lista', 'por_aprobar', 'pendiente'], queryFn: () => api<SeparationListItem[]>('/api/usuarios/bajas?alcance=por_aprobar&estado=pendiente') });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-tinta">{t('us.portal')}</h1>
        <p className="max-w-2xl text-sm text-slate-600">{t('us.portal_descripcion')}</p>
      </div>
      <PeopleNav />
      <div className="grid gap-4 md:grid-cols-2">
        <Card title={t('us.portal_por_aprobar')}>
          <ul className="space-y-2 text-sm">
            <li>
              <Link to="/usuarios/altas?alcance=por_aprobar&estado=pendiente" className="text-marca hover:underline">
                {t('us.portal_altas_pendientes', { n: hires.data?.length ?? 0 })}
              </Link>
            </li>
            <li>
              <Link to="/usuarios/bajas?alcance=por_aprobar&estado=pendiente" className="text-marca hover:underline">
                {t('us.portal_bajas_pendientes', { n: seps.data?.length ?? 0 })}
              </Link>
            </li>
          </ul>
        </Card>
        <Card title={t('us.portal_solicitar')}>
          <div className="flex flex-wrap gap-2">
            <Link to="/usuarios/altas/nueva" className="rounded bg-marca px-4 py-2 text-sm font-medium text-white hover:bg-marca-claro">
              {t('us.altas_nueva')}
            </Link>
            <Link to="/usuarios/bajas/nueva" className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {t('us.bajas_nueva')}
            </Link>
          </div>
        </Card>
      </div>
      <ResetSearch />
    </div>
  );
}

/** Buscar a la persona y pedir el reset: crea un ticket para IT a nombre de RR. HH. */
function ResetSearch() {
  const me = useMe();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  const users = useQuery({
    queryKey: ['usuarios', 'directorio', debounced, 'activo'],
    queryFn: () => api<DirectoryUser[]>(`/api/usuarios?${new URLSearchParams({ buscar: debounced, estado: 'activo' })}`),
    enabled: debounced.length >= 2,
  });
  const found = users.data?.filter((u) => isHr(me, u.organizationId)).slice(0, 10);

  return (
    <Card title={t('us.portal_reset')}>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">{t('us.portal_reset_ayuda')}</p>
        <div className="max-w-sm">
          <Field id="us-portal-buscar" type="search" label={t('us.buscar')} placeholder={t('us.buscar_ayuda')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {debounced.length >= 2 &&
          (!found ? (
            users.error ? <Alert>{errorMessage(users.error)}</Alert> : <Spinner label={t('comun.cargando')} />
          ) : found.length === 0 ? (
            <p className="text-sm text-slate-500">{t('us.directorio_vacio')}</p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-md border border-slate-200" aria-label={t('us.resultados')}>
              {found.map((u) => (
                <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                  <span>
                    <span className="font-medium text-slate-900">{u.fullName}</span>
                    <span className="block text-xs text-slate-500">
                      {u.email} · {u.organization}
                    </span>
                  </span>
                  <ResetActions userId={u.id} fullName={u.fullName} />
                </li>
              ))}
            </ul>
          ))}
      </div>
    </Card>
  );
}
